import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  MapPin,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';

import { signupSchema, type SignupFormData } from '@/lib/validations';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Checkbox } from '@/components/atoms/checkbox';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/molecules/form-field';
import { useCountries } from '@/lib/hooks/use-countries';

function getPasswordStrength(pwd: string): { score: number; label: string; colorClass: string } {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[@$!%*?&.,;:_\-+=#^(){}[\]<>]/.test(pwd)) score++;
  const map = [
    { label: 'auth.passwordStrength.veryWeak', colorClass: 'bg-destructive' },
    { label: 'auth.passwordStrength.weak', colorClass: 'bg-destructive' },
    { label: 'auth.passwordStrength.fair', colorClass: 'bg-yellow-500' },
    { label: 'auth.passwordStrength.good', colorClass: 'bg-yellow-500' },
    { label: 'auth.passwordStrength.strong', colorClass: 'bg-green-500' },
    { label: 'auth.passwordStrength.veryStrong', colorClass: 'bg-green-600' },
  ];
  return { score, ...map[score] };
}

interface SignupFormProps {
  onSubmit: (data: SignupFormData) => Promise<void>;
  isLoading: boolean;
  error?: string | null;
  onErrorClear?: () => void;
}

type StepKey = 1 | 2 | 3;

const STEP1_FIELDS = ['name', 'email', 'birthday', 'password', 'confirmPassword'] as const;
const STEP2_FIELDS = ['countryIsoCode3', 'countryName', 'city'] as const;

export function SignupForm({ onSubmit, isLoading, error, onErrorClear }: SignupFormProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<StepKey>(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const { data: countries = [] } = useCountries();

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    mode: 'onTouched',
    defaultValues: {
      email: '',
      name: '',
      birthday: '',
      password: '',
      confirmPassword: '',
      city: '',
      address: '',
      addressLine2: '',
      postalCode: '',
      landmark: '',
      countryIsoCode3: '',
      countryName: '',
      latitude: undefined,
      longitude: undefined,
      acceptTerms: false,
    },
  });

  const passwordValue = form.watch('password') || '';
  const passwordStrength = getPasswordStrength(passwordValue);
  const latitude = form.watch('latitude');
  const longitude = form.watch('longitude');
  const hasGpsCoordinates = latitude != null && longitude != null;

  // Values used in the recap (step 3)
  const nameValue = form.watch('name');
  const emailValue = form.watch('email');
  const cityValue = form.watch('city');
  const countryNameValue = form.watch('countryName');

  const handleUseMyLocation = () => {
    if (!('geolocation' in navigator)) {
      setGeoError(t('auth.geolocation.notSupported'));
      return;
    }
    setGeoLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        form.setValue('latitude', position.coords.latitude, { shouldValidate: true });
        form.setValue('longitude', position.coords.longitude, { shouldValidate: true });
        setGeoLoading(false);
      },
      (err) => {
        setGeoLoading(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setGeoError(t('auth.geolocation.permissionDenied'));
            break;
          case err.POSITION_UNAVAILABLE:
            setGeoError(t('auth.geolocation.positionUnavailable'));
            break;
          case err.TIMEOUT:
            setGeoError(t('auth.geolocation.timeout'));
            break;
          default:
            setGeoError(t('auth.geolocation.error'));
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleClearLocation = () => {
    form.setValue('latitude', undefined, { shouldValidate: true });
    form.setValue('longitude', undefined, { shouldValidate: true });
    setGeoError(null);
  };

  const handleNext = async () => {
    onErrorClear?.();
    let fieldsToValidate: readonly (keyof SignupFormData)[] = [];
    if (currentStep === 1) fieldsToValidate = STEP1_FIELDS;
    if (currentStep === 2) fieldsToValidate = STEP2_FIELDS;

    const isValid = await form.trigger(fieldsToValidate as (keyof SignupFormData)[]);
    if (!isValid) return;
    setCurrentStep((prev) => (Math.min(prev + 1, 3) as StepKey));
  };

  const handleBack = () => {
    onErrorClear?.();
    setCurrentStep((prev) => (Math.max(prev - 1, 1) as StepKey));
  };

  const handleSubmit = async (data: SignupFormData) => {
    onErrorClear?.();
    await onSubmit(data);
  };

  const stepTitles: Record<StepKey, string> = {
    1: t('auth.wizard.step1Title'),
    2: t('auth.wizard.step2Title'),
    3: t('auth.wizard.step3Title'),
  };

  return (
    <div className="w-full max-w-md space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">{t('auth.signupTitle')}</h1>
        <p className="text-muted-foreground mt-2">{t('auth.signupSubtitle')}</p>
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {t('auth.wizard.stepIndicator', { current: currentStep, total: 3 })}
          </span>
          <span className="font-medium text-foreground">{stepTitles[currentStep]}</span>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full ${
                s <= currentStep ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          {/* ---------- Step 1: Personal info ---------- */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.name')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.email')}</FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="birthday"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.birthday')}</FormLabel>
                    <FormControl>
                      <Input type="date" autoComplete="bday" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.password')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          className="pr-10"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    {passwordValue && (
                      <div className="mt-1 space-y-1">
                        <div className="flex h-1 gap-1">
                          {[0, 1, 2, 3, 4].map((i) => (
                            <div
                              key={i}
                              className={`flex-1 rounded-full ${
                                i < passwordStrength.score ? passwordStrength.colorClass : 'bg-muted'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">{t(passwordStrength.label)}</p>
                      </div>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.confirmPassword')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          className="pr-10"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* ---------- Step 2: Location ---------- */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="countryIsoCode3"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.country')}</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        const selected = countries.find((c) => c.isoCode3 === value);
                        if (selected) {
                          form.setValue('countryName', selected.name, { shouldValidate: true });
                        }
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('shops.selectCountry')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem key={country.isoCode3} value={country.isoCode3}>
                            {country.flagEmoji ? `${country.flagEmoji} ` : ''}
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <input type="hidden" {...form.register('countryName')} />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.city')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="address-level2" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* GPS block */}
              <div className="rounded-md border border-dashed border-border p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{t('auth.geolocation.title')}</span>
                  </div>
                  {hasGpsCoordinates ? (
                    <button
                      type="button"
                      onClick={handleClearLocation}
                      className="inline-flex items-center gap-1 text-xs text-destructive hover:underline"
                    >
                      <X className="h-3 w-3" />
                      {t('auth.geolocation.clear')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleUseMyLocation}
                      disabled={geoLoading}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
                    >
                      {geoLoading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <MapPin className="h-3 w-3" />
                      )}
                      {t('auth.geolocation.useMyLocation')}
                    </button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('auth.geolocation.description')}
                </p>
                {hasGpsCoordinates && (
                  <p className="text-xs text-green-600">
                    {t('auth.geolocation.captured', {
                      lat: latitude?.toFixed(6),
                      lng: longitude?.toFixed(6),
                    })}
                  </p>
                )}
                {geoError && <p className="text-xs text-destructive">{geoError}</p>}

                {hasGpsCoordinates && (
                  <FormField
                    control={form.control}
                    name="landmark"
                    render={({ field }) => (
                      <FormItem className="pt-2">
                        <FormLabel>{t('auth.landmark')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('auth.landmarkPlaceholder')} {...field} />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">{t('auth.landmarkHint')}</p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {/* Collapsible "More details" */}
              <div className="rounded-md border border-border">
                <button
                  type="button"
                  onClick={() => setShowMoreDetails((v) => !v)}
                  className="flex w-full items-center justify-between p-3 text-sm font-medium"
                >
                  <span>{t('auth.wizard.moreDetails')}</span>
                  {showMoreDetails ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>
                {showMoreDetails && (
                  <div className="space-y-4 border-t border-border p-3">
                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.address')}</FormLabel>
                          <FormControl>
                            <Input
                              autoComplete="address-line1"
                              placeholder={t('auth.addressPlaceholder')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="addressLine2"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.addressLine2')}</FormLabel>
                          <FormControl>
                            <Input
                              autoComplete="address-line2"
                              placeholder={t('auth.addressLine2Placeholder')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="postalCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.postalCode')}</FormLabel>
                          <FormControl>
                            <Input autoComplete="postal-code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ---------- Step 3: Review & confirm ---------- */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="rounded-md border border-border p-4 space-y-2 text-sm">
                <p className="font-medium">{t('auth.wizard.recapTitle')}</p>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('auth.name')}</span>
                  <span className="font-medium">{nameValue || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('auth.email')}</span>
                  <span className="font-medium">{emailValue || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('auth.city')}</span>
                  <span className="font-medium">{cityValue || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('auth.country')}</span>
                  <span className="font-medium">{countryNameValue || '—'}</span>
                </div>
                {hasGpsCoordinates && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('auth.geolocation.title')}
                    </span>
                    <span className="font-medium text-green-600">✓</span>
                  </div>
                )}
              </div>

              <FormField
                control={form.control}
                name="acceptTerms"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <Label className="cursor-pointer text-sm leading-relaxed block">
                      <span className="block">{t('auth.acceptTermsPrefix')}</span>
                      <span className="block">
                        <Link
                          to="/terms-of-use/pro"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {t('auth.termsOfUse')}
                        </Link>
                        {', '}
                        <Link
                          to="/privacy-policy/pro"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {t('auth.privacyPolicy')}
                        </Link>
                      </span>
                      <span className="block">
                        {t('auth.and')}{' '}
                        <Link
                          to="/cgv/pro"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {t('auth.cgv')}
                        </Link>
                      </span>
                    </Label>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {error && (
            <div className="text-destructive text-sm text-center p-2 bg-destructive/10 rounded-md">
              {error}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center gap-3 pt-2">
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="flex-1"
                disabled={isLoading}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t('auth.wizard.back')}
              </Button>
            )}
            {currentStep < 3 ? (
              <Button type="button" onClick={handleNext} className="flex-1">
                {t('auth.wizard.next')}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button type="submit" className="flex-1" loading={isLoading}>
                {t('auth.signup')}
              </Button>
            )}
          </div>
        </form>
      </Form>

      <p className="text-center text-sm text-muted-foreground">
        {t('auth.alreadyHaveAccount')}{' '}
        <Link to="/login" className="text-primary hover:underline">
          {t('auth.login')}
        </Link>
      </p>
    </div>
  );
}
